import { useState, useCallback, useEffect, useMemo } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChannelAvatar,
  Dialog,
  Flex,
  Skeleton,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { FilterInput } from '~publish/components/FilterInput'
import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import { useRefreshOwnAccess } from '../../../shared/hooks/useRefreshOwnAccess'
import type {
  AccessLevel,
  ProductAccessLevels,
  ProductKey,
} from '../../../Team/types'
import { getDisplayName } from '../../../Team/utils/getDisplayName'
import { areProductLevelsEqual } from '../../../Team/utils/productAccessLevels'
import { useTeamMembersForChannel } from '../../hooks/useTeamMembersForChannel'
import { useUpdateChannelMemberPermissions } from '../../hooks/useUpdateChannelMemberPermissions'

import { ChannelMemberPermissionsTable } from './ChannelMemberPermissionsTable'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'

import styles from './EditChannelPermissionsDialog.module.css'

const NO_ACCESS: ProductAccessLevels = { publish: 'none', comments: 'none' }

// Above this many team members, show a search box to filter the list. Smaller
// lists are easy to scan, so the input would just be noise.
const MEMBER_SEARCH_THRESHOLD = 5

interface ChannelInfo {
  id: string
  name: string
  avatar: string | null
  service: string
}

interface EditChannelPermissionsDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose: () => void
  channel: ChannelInfo | null
}

export const EditChannelPermissionsDialog = ({
  isOpen,
  onOpenChange,
  onClose,
  channel,
}: EditChannelPermissionsDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const viewerAccountId = useAccountId()
  const refreshOwnAccess = useRefreshOwnAccess()

  // A per-product grant per member.
  const [memberProductLevels, setMemberProductLevels] = useState<
    Map<string, ProductAccessLevels>
  >(new Map())
  // View-only filter over the member list; never affects which members are
  // bulk-edited or submitted — those always operate on `teamMembersForChannel`.
  const [memberSearch, setMemberSearch] = useState('')

  // Only fetch team members when the dialog is open
  const { teamMembersForChannel, loading: loadingMembers } =
    useTeamMembersForChannel({
      organizationId: organizationId ?? '',
      channelId: channel?.id ?? '',
      skip: !isOpen,
    })

  const showMemberSearch =
    (teamMembersForChannel?.length ?? 0) > MEMBER_SEARCH_THRESHOLD

  const filteredMembers = useMemo(() => {
    if (!teamMembersForChannel) return teamMembersForChannel
    const term = memberSearch.trim().toLowerCase()
    if (!term) return teamMembersForChannel
    return teamMembersForChannel.filter(({ teamMember }) => {
      const displayName = getDisplayName(teamMember).toLowerCase()
      return (
        displayName.includes(term) ||
        teamMember.email.toLowerCase().includes(term)
      )
    })
  }, [teamMembersForChannel, memberSearch])

  const { updateChannelMemberPermissions, loading: updating } =
    useUpdateChannelMemberPermissions()

  // Initialize form state when data loads
  useEffect(() => {
    if (!teamMembersForChannel) return

    const productLevels = new Map<string, ProductAccessLevels>()
    teamMembersForChannel.forEach((item) => {
      productLevels.set(item.teamMember.id, item.productAccessLevels)
    })
    setMemberProductLevels(productLevels)
  }, [teamMembersForChannel])

  const handleClose = useCallback(() => {
    setMemberSearch('')
    onClose()
  }, [onClose])

  // Funnel every dismissal (Cancel, Escape, backdrop) through `handleClose` so
  // the member filter always resets. Passing `onOpenChange` straight to Dialog
  // would skip the reset on Escape/backdrop.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpenChange(true)
      } else {
        handleClose()
      }
    },
    [onOpenChange, handleClose],
  )

  const handleProductLevelChange = useCallback(
    (accountId: string, product: ProductKey, level: AccessLevel) => {
      setMemberProductLevels((prev) => {
        const next = new Map(prev)
        const current = next.get(accountId) ?? NO_ACCESS
        next.set(accountId, { ...current, [product]: level })
        return next
      })
    },
    [],
  )

  const handleSubmit = async (): Promise<void> => {
    if (!organizationId || !channel || !teamMembersForChannel) return

    try {
      // Only send members whose per-product grant changed.
      const productMemberPermissions = teamMembersForChannel
        .filter((item) => {
          const next = memberProductLevels.get(item.teamMember.id) ?? NO_ACCESS
          return !areProductLevelsEqual(next, item.productAccessLevels)
        })
        .map((item) => ({
          accountId: item.teamMember.id,
          productAccessLevels:
            memberProductLevels.get(item.teamMember.id) ?? NO_ACCESS,
        }))

      if (productMemberPermissions.length === 0) {
        handleClose()
        return
      }

      const ownAccessChanged = productMemberPermissions.some(
        (permission) => permission.accountId === viewerAccountId,
      )

      const result = await updateChannelMemberPermissions({
        organizationId,
        channelId: channel.id,
        productMemberPermissions,
      })

      if (result.success) {
        toast.success('Channel permissions updated successfully')
        handleClose()
        // An admin editing their own access to this channel invalidates most
        // of what's cached for them (sidebar channels, Community actions,
        // user menu) — refresh it all rather than let it go stale.
        if (ownAccessChanged) {
          void refreshOwnAccess()
        }
      } else {
        toast.error(result.error ?? 'Failed to update permissions')
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to update permissions: ${errorMessage}`)
    }
  }

  if (!channel) return <></>

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content size="large" className={styles.dialogContent}>
        <Dialog.Header>
          <Dialog.Title>Edit Channel Permissions</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className={styles.form}>
            {/* Channel info header */}
            <Flex className={styles.channelHeader} align="center" gap="md">
              {isChannelIconService(channel.service) && (
                <ChannelAvatar
                  src={channel.avatar ?? undefined}
                  alt={channel.name}
                  channel={channel.service}
                  size="large"
                />
              )}
              <Flex direction="column" gap="2xs">
                <Text weight="bold" size="lg">
                  {channel.name}
                </Text>
                <Text color="subtle" size="sm">
                  Manage which team members can access this channel
                </Text>
              </Flex>
            </Flex>

            {/* Per-member access. No section heading — the channel header
                above already says what this dialog manages (CORE-6241). */}
            <div className={styles.formField}>
              {loadingMembers ? (
                <Flex direction="column" gap="xs">
                  <Skeleton height="48px" width="100%" />
                  <Skeleton height="48px" width="100%" />
                  <Skeleton height="48px" width="100%" />
                </Flex>
              ) : teamMembersForChannel?.length ? (
                <Flex direction="column" gap="xs" align="stretch" fullWidth>
                  {showMemberSearch && (
                    <FilterInput
                      value={memberSearch}
                      onValueChange={setMemberSearch}
                      placeholder="Search by name or email"
                      aria-label="Search team members by name or email"
                    />
                  )}
                  {filteredMembers && filteredMembers.length > 0 ? (
                    <ChannelMemberPermissionsTable
                      members={filteredMembers}
                      levelsByMember={memberProductLevels}
                      onChange={handleProductLevelChange}
                    />
                  ) : (
                    <Text color="subtle">No team members found</Text>
                  )}
                </Flex>
              ) : (
                <Text color="subtle">No team members available</Text>
              )}
            </div>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={updating}
            loading={updating}
          >
            Save Changes
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
